import { add, cross, scale, UP, type Vec3 } from '@rcs/domain';

/** 車両の位置と姿勢（前方 T・上方 U・右方 R = T × U）。 */
export interface VehiclePose {
  readonly position: Vec3;
  readonly tangent: Vec3;
  readonly up: Vec3;
  readonly right: Vec3;
}

/** カメラの位置・注視点・上方向。 */
export interface CameraPose {
  readonly position: Vec3;
  readonly target: Vec3;
  readonly up: Vec3;
}

/** 追従俯瞰カメラの位置（FR-CAM-006）：車両の後方・前方・側方。 */
export type FollowDirection = 'behind' | 'front' | 'side';

/** 追従の方向の一覧（表示順）。 */
export const FOLLOW_DIRECTIONS: readonly FollowDirection[] = ['behind', 'front', 'side'];

/** 追従カメラの既定の距離 [m]。 */
export const DEFAULT_FOLLOW_DISTANCE = 14;

/** 追従カメラの既定の高さ [m]（世界の上方向）。 */
export const DEFAULT_FOLLOW_HEIGHT = 4;

/** カメラが地面より下へ潜らない最低の高さ [m]。 */
const MIN_CAMERA_HEIGHT = 0.5;

/**
 * 追従俯瞰カメラの姿勢。車両から一定の距離・高さを保ち、車両を見る。
 *
 * - `behind`：後方（進行方向と逆）から追う
 * - `front`：前方から車両を迎える向きで見る
 * - `side`：右側方から見る
 *
 * 高さは世界の上方向で足し、カメラの上方向も世界の上方向にする（ループでも画面が回らない）。
 * 地面（y = 0）より下へは潜らない。
 */
export function followCameraPose(
  vehicle: VehiclePose,
  direction: FollowDirection,
  distance: number = DEFAULT_FOLLOW_DISTANCE,
  height: number = DEFAULT_FOLLOW_HEIGHT,
  targetHeight = 0.5,
): CameraPose {
  const along: Vec3 =
    direction === 'behind'
      ? scale(vehicle.tangent, -distance)
      : direction === 'front'
        ? scale(vehicle.tangent, distance)
        : scale(cross(vehicle.tangent, vehicle.up), distance);
  const raised = add(add(vehicle.position, along), scale(UP, height));
  const position: Vec3 = [raised[0], Math.max(raised[1], MIN_CAMERA_HEIGHT), raised[2]];
  // 車両の車体の中心（レールの `targetHeight` 上。既定 0.5 m）を見る
  const target = add(vehicle.position, scale(UP, targetHeight));
  return { position, target, up: UP };
}

/** 乗客視点で見回している向き（FR-CAM-001）。車両の前方からのずれ。 */
export interface LookAngles {
  /** 水平方向 [rad]。0 が前方、正が右 */
  readonly yaw: number;
  /** 鉛直方向 [rad]。0 が水平、正が上 */
  readonly pitch: number;
}

/** 見回せる範囲：左右 120°、上下 70°。 */
export const LOOK_LIMITS = {
  maxYaw: (120 * Math.PI) / 180,
  maxPitch: (70 * Math.PI) / 180,
} as const;

/** 乗客の目の高さ [m]（レールから上方向へ。ハートライン 1.1 m の少し上）。 */
export const PASSENGER_EYE_HEIGHT = 1.35;

/** 乗客視点の視野角 [度]。 */
export const PASSENGER_FOV = 75;

/** 見回す向きを、範囲 {@link LOOK_LIMITS} に収める。 */
export function clampLook(look: LookAngles): LookAngles {
  return {
    yaw: Math.min(LOOK_LIMITS.maxYaw, Math.max(-LOOK_LIMITS.maxYaw, look.yaw)),
    pitch: Math.min(LOOK_LIMITS.maxPitch, Math.max(-LOOK_LIMITS.maxPitch, look.pitch)),
  };
}

/**
 * 乗客視点のカメラ姿勢：座席（レールの上 `eyeHeight`、既定 {@link PASSENGER_EYE_HEIGHT}）から、前方を見る。
 * `look` で見回した向きにずらす。カメラの上方向は車両の上方向（バンクや逆さまに追従する）。
 */
export function passengerCameraPose(
  vehicle: VehiclePose,
  look: LookAngles = { yaw: 0, pitch: 0 },
  eyeHeight: number = PASSENGER_EYE_HEIGHT,
): CameraPose {
  const { yaw, pitch } = clampLook(look);
  const position = add(vehicle.position, scale(vehicle.up, eyeHeight));
  const right = cross(vehicle.tangent, vehicle.up);
  const horizontal = add(scale(vehicle.tangent, Math.cos(yaw)), scale(right, Math.sin(yaw)));
  const forward = add(scale(horizontal, Math.cos(pitch)), scale(vehicle.up, Math.sin(pitch)));
  return { position, target: add(position, scale(forward, 10)), up: vehicle.up };
}
